/**
 * Ovani affiliate attribution — site-wide.
 * Reads ?ref= / ?aff= / ?affiliate=, records click, sets cookie, syncs cart attributes.
 * Also powers the cart "referral code" form via [data-ovani-aff-code-form].
 */
(function () {
  const COOKIE_NAME = 'ovani_aff';
  const STORAGE_KEY = 'ovani_aff_attr';

  function workerUrl() {
    const el =
      document.querySelector('script[data-ovani-affiliate-worker]') ||
      document.querySelector('[data-ovani-affiliate-worker]');
    const fromData = el && el.getAttribute('data-ovani-affiliate-worker');
    if (fromData) return fromData.replace(/\/$/, '');
    if (window.ovaniAffiliateWorkerUrl) return String(window.ovaniAffiliateWorkerUrl).replace(/\/$/, '');
    return 'https://ovani-bundle-builder.sam-800.workers.dev';
  }

  function readCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  function writeCookie(name, value, days) {
    const maxAge = Math.max(1, Number(days) || 30) * 86400;
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      name +
      '=' +
      encodeURIComponent(value) +
      '; Path=/; Max-Age=' +
      maxAge +
      '; SameSite=Lax' +
      secure;
  }

  function getStoredAttr() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY) || readCookie(COOKIE_NAME);
      if (!raw) return null;
      if (raw.startsWith('{')) return JSON.parse(raw);
      // legacy: code only
      return { code: raw };
    } catch {
      return null;
    }
  }

  function storeAttr(attr, days) {
    const payload = {
      affiliate_id: attr.affiliate_id || '',
      code: attr.code || '',
      slug: attr.slug || '',
      ts: Date.now(),
    };
    const encoded = JSON.stringify(payload);
    try {
      localStorage.setItem(STORAGE_KEY, encoded);
    } catch (_) {}
    writeCookie(COOKIE_NAME, encoded, days);
    window.ovaniAffiliate = payload;
    return payload;
  }

  async function syncCartAttributes(attr) {
    if (!attr || (!attr.affiliate_id && !attr.code)) return;
    try {
      await fetch((window.routes && window.routes.cart_update_url) || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: {
            affiliate_id: attr.affiliate_id || '',
            affiliate_code: attr.code || '',
            affiliate_slug: attr.slug || '',
          },
        }),
      });
    } catch (err) {
      console.warn('[ovani-affiliate] cart attribute sync failed', err);
    }
  }

  async function attributeRef(ref, { path, recordClick } = {}) {
    const cleaned = String(ref || '').trim();
    if (!cleaned) return null;
    const endpoint = recordClick === false ? '/affiliate/resolve' : '/affiliate/click';
    const response = await fetch(workerUrl() + endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ref: cleaned,
        path: path || location.pathname + location.search,
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Could not apply referral');
    const attr = storeAttr(data, data.cookie_days || 30);
    await syncCartAttributes(attr);
    return attr;
  }

  function paramRef() {
    const params = new URLSearchParams(location.search);
    return params.get('ref') || params.get('aff') || params.get('affiliate') || '';
  }

  function bindCodeForms() {
    document.querySelectorAll('[data-ovani-aff-code-form]').forEach((form) => {
      if (form.dataset.bound === '1') return;
      form.dataset.bound = '1';
      const input = form.querySelector('[data-ovani-aff-code-input]');
      const status = form.querySelector('[data-ovani-aff-code-status]');
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const code = (input && input.value) || '';
        if (status) status.textContent = 'Applying…';
        try {
          const attr = await attributeRef(code, { recordClick: true });
          if (status) {
            status.textContent = attr ? 'Referral applied: ' + (attr.code || attr.slug) : 'Applied.';
          }
          if (input) input.value = '';
        } catch (err) {
          if (status) status.textContent = err.message || 'Invalid code';
        }
      });
    });
  }

  function showActiveBadge() {
    const attr = getStoredAttr();
    document.querySelectorAll('[data-ovani-aff-active]').forEach((el) => {
      if (attr && (attr.code || attr.slug)) {
        el.hidden = false;
        el.textContent = 'Referral: ' + (attr.code || attr.slug);
      }
    });
  }

  // Expose for Bundle Builder cart adds
  window.ovaniAffiliateGet = getStoredAttr;
  window.ovaniAffiliateAttribute = attributeRef;
  window.ovaniAffiliateSyncCart = async function () {
    const attr = getStoredAttr();
    if (attr) await syncCartAttributes(attr);
    return attr;
  };

  async function boot() {
    bindCodeForms();
    const fromUrl = paramRef();
    try {
      if (fromUrl) {
        await attributeRef(fromUrl, { recordClick: true });
        // Clean ref params from the address bar without reload
        try {
          const url = new URL(location.href);
          ['ref', 'aff', 'affiliate'].forEach((k) => url.searchParams.delete(k));
          history.replaceState({}, '', url.pathname + url.search + url.hash);
        } catch (_) {}
      } else {
        const existing = getStoredAttr();
        if (existing) {
          window.ovaniAffiliate = existing;
          await syncCartAttributes(existing);
        }
      }
    } catch (err) {
      console.warn('[ovani-affiliate]', err);
    }
    showActiveBadge();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
